import { localeCompare, pxToDP, useIntl, useSportCategoriesQuery } from '@capsicum/common';
import { BaseSearchInput, ChipList, type ChipListItem, Text, TOPBAR_HEIGHT } from '@capsicum/common-web';
import {
	type SelectFavouriteTeamSelectionVariants,
	type SelectFavouriteTeamValue,
	useSelectFavouriteTeam
} from '@capsicum/socios-core';
import { rgba } from 'polished';
import { useCallback, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { KeyboardAwareBottomWrapper } from '../../../auth/KeyboardAwareBottomWrapper';
import { LoaderActivities } from '../../../feeds/ui/LoaderActivities';
import { useTeamsWidthCalculation } from '../../hooks';
import { useSportsIcon } from '../hooks';

import { EmptyResults } from './EmptyResults';
import { FooterPageContainer } from './FooterPageContainer';
import { FooterWidgetContainer } from './FooterWidgetContainer';
import { PageWrapper } from './PageWrapper';
import { SportFiltersSkeleton, TeamSkeleton } from './SelectFavouriteTeam.skeletons';
import { WidgetWrapper } from './SelectFavouriteTeam.styled';
import { SelectorTeam } from './SelectorTeam';

type SelectFavouriteTeamCommonProps = {
	hasTopbar?: boolean;
	initialValue?: SelectFavouriteTeamValue;
	onSubmit?: (payload: SelectFavouriteTeamValue) => void;
	persist?: boolean;
	selection?: SelectFavouriteTeamSelectionVariants;
};

type SelectFavouriteTeamPageProps = SelectFavouriteTeamCommonProps & {
	layout: 'page';
};

type SelectFavouriteTeamWidgetProps = SelectFavouriteTeamCommonProps & {
	layout: 'widget';
	onSkip?: () => void;
};

type SelectFavouriteTeamProps = SelectFavouriteTeamPageProps | SelectFavouriteTeamWidgetProps;

export const SelectFavouriteTeam = (props: SelectFavouriteTeamProps) => {
	const {
		hasTopbar = false,
		initialValue,
		layout,
		onSubmit: onSubmitProp,
		persist = true,
		selection = 'favourite'
	} = props;

	// hooks
	const { $t } = useIntl();
	const getSportsIcon = useSportsIcon();
	const [nameFilter, setNameFilter] = useState('');
	const [isSearchFocused, setSearchFocused] = useState(false);
	const [sportsFilter, setSportsFilter] = useState<Array<string>>([]);
	const { data: sportCategories, isLoading: isSportCategoriesLoading } = useSportCategoriesQuery();

	const {
		allPartners,
		getOptionSelectionState,
		isLoading,
		isOptionDisabled,
		isSubmittable,
		onSubmit,
		onTeamSelection,
		partners,
		selectedFavouritePartnerID
	} = useSelectFavouriteTeam({
		initialValue,
		LoaderContent: LoaderActivities,
		nameFilter,
		onSubmit: onSubmitProp,
		persist,
		selection,
		sportsFilter
	});

	// state
	const {
		breakpoints: {
			points: {
				mobile: { maxScreenWidth }
			}
		},
		spacings: { small, xsmall }
	} = useTheme();

	const Wrapper = layout === 'page' ? PageWrapper : WidgetWrapper;

	const resultsGap = pxToDP(xsmall);
	const resultsPadding = pxToDP(small);
	const {
		elementRef,
		isSmall,
		teamWidth,
		width: resultsContainerWidth
	} = useTeamsWidthCalculation({
		getTeamsPerRow: useCallback((width: number) => (width > 520 ? 6 : 4), []),
		resultsGap,
		resultsPadding
	});

	const bottomContentWidth = resultsContainerWidth === 0 ? maxScreenWidth : resultsContainerWidth;

	// callbacks
	const onSearchFocus = useCallback(() => {
		setSearchFocused(true);
	}, []);

	const onSearchBlur = useCallback(() => {
		setSearchFocused(false);
	}, []);

	// useMemos
	const selectedFavouritePartner = useMemo(() => {
		return !selectedFavouritePartnerID || layout !== 'page'
			? null
			: allPartners.find(partner => partner.id === selectedFavouritePartnerID);
	}, [layout, allPartners, selectedFavouritePartnerID]);

	const sports = useMemo(
		() =>
			!sportCategories
				? []
				: sportCategories
						.reduce<Array<ChipListItem>>((sports, sport) => {
							const { code, id, name, translations } = sport;

							const translatedName = translations?.find(({ field }) => field === 'name')?.value;

							if (id && name) {
								sports.push({
									description: translatedName ?? name,
									iconStart: getSportsIcon(code),
									name: translatedName ?? name,
									value: id
								});
							}

							return sports;
						}, [])
						.sort((sportA, sportB) => localeCompare(sportA.name, sportB.name)),
		[getSportsIcon, sportCategories]
	);

	const results = useMemo(() => {
		if (isLoading && partners.length === 0) {
			return Array(16)
				.fill(null)
				.map((_, index) => <TeamSkeleton height={SelectorTeam.HEIGHT} key={index} width={teamWidth} />);
		}

		if (!partners) return [];

		return partners.reduce<Array<JSX.Element>>((results, partner) => {
			const { id: partnerID } = partner;

			if (partnerID) {
				results.push(
					<SelectorTeam
						disabled={isOptionDisabled(partnerID)}
						image={partner.images?.partnerLogo?.url}
						isSmall={isSmall}
						key={partnerID}
						name={partner.name}
						partnerID={partnerID}
						selection={getOptionSelectionState(partnerID)}
						width={teamWidth}
						onClick={onTeamSelection}
					/>
				);
			}

			return results;
		}, []);
	}, [isSmall, partners, isLoading, teamWidth, onTeamSelection, isOptionDisabled, getOptionSelectionState]);

	// useEffects
	const handleSearch = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const text = event.target.value || '';

			setNameFilter(text);
		},
		[setNameFilter]
	);

	// JSX
	return (
		<>
			<Wrapper>
				<S.Content>
					<S.Results $gap={resultsGap} $padding={resultsPadding} ref={elementRef}>
						{results.length === 0 ? <EmptyResults /> : results}
					</S.Results>

					<S.Search $hasTopbar={hasTopbar} $variant={layout}>
						<S.SearchField>
							<BaseSearchInput
								debounceTime={500}
								placeholder={$t('selectFavouriteTeam.search')}
								testID="selectFavouriteTeam-nameFilter-input"
								onBlur={onSearchBlur}
								onChange={handleSearch}
								onFocus={onSearchFocus}
							/>
						</S.SearchField>

						<S.ChipListWrapper>
							{isSportCategoriesLoading ? (
								<SportFiltersSkeleton />
							) : (
								<ChipList
									hasWrapper={false}
									items={sports}
									itemsSelected={setSportsFilter}
									selectionType="multiple"
								/>
							)}
						</S.ChipListWrapper>
					</S.Search>

					<S.Header $maxHeight={isSearchFocused ? 0 : 500}>
						<S.HeaderContent>
							<Text variant="text1">{$t('selectFavouriteTeam.overline')}</Text>
							<S.HeaderTitle variant="text8">
								{$t(`selectFavouriteTeam.title.${selection}`)}
							</S.HeaderTitle>

							{/* This will be re-enabled at a later stage  */}
							{/* <Text variant="text5">
								{$t(`selectFavouriteTeam.description.${selection}`, {
									bold: (chunks: Array<ReactNode>) => <BoldText>{chunks}</BoldText>
								})}
							</Text> */}
						</S.HeaderContent>
					</S.Header>
				</S.Content>
			</Wrapper>

			<KeyboardAwareBottomWrapper>
				{props.layout === 'page' ? (
					<FooterPageContainer
						isSubmittable={isSubmittable}
						maxWidth={bottomContentWidth}
						selectedPartner={selectedFavouritePartner}
						selection={selection}
						onSubmit={onSubmit}
					/>
				) : (
					<FooterWidgetContainer
						isSubmittable={isSubmittable}
						maxWidth={bottomContentWidth}
						onSkip={props.onSkip}
						onSubmit={onSubmit}
					/>
				)}
			</KeyboardAwareBottomWrapper>
		</>
	);
};

namespace S {
	export const Content = styled.div`
		flex: 1;
		width: 100%;
		display: flex;
		flex-direction: column-reverse;
	`;

	export const Header = styled.div<{ $maxHeight: number }>`
		overflow: hidden;
		transition: max-height 0.5s ease-in;
		max-height: ${({ $maxHeight }) => $maxHeight}px;
	`;

	export const HeaderContent = styled.div`
		justify-content: flex-end;
		padding-top: ${({ theme }) => theme.spacings.xlarge};
		padding-left: ${({ theme }) => theme.spacings.small};
		padding-right: ${({ theme }) => theme.spacings.small};
		padding-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const HeaderTitle = styled(Text)`
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const HeaderDescription = styled(Text)`
		${({ theme }) => theme.fonts.text.text5};
	`;

	export const Search = styled.div<{ $hasTopbar: boolean; $variant: SelectFavouriteTeamProps['layout'] }>`
		position: sticky;
		padding: ${({ theme }) => theme.spacings.medium} 0;
		margin-bottom: ${({ theme }) => theme.spacings.large};
		top: ${({ $hasTopbar }) => ($hasTopbar ? TOPBAR_HEIGHT : 0)}px;
		background-color: ${({ $variant, theme }) =>
			$variant === 'widget'
				? theme.colors.backgrounds.main.translucent
				: rgba(theme.colors.backgrounds.main.screen, 0.9)};
	`;

	export const ChipListWrapper = styled.div`
		padding: 0 ${({ theme }) => theme.spacings.small};
		margin-top: ${({ theme }) => theme.spacings.large};
	`;

	export const SearchField = styled.div`
		padding: 0 ${({ theme }) => theme.spacings.small};
	`;

	export const Results = styled.div<{ $gap: number; $padding: number }>`
		flex: 1;
		display: flex;
		flex-wrap: wrap;
		flex-direction: row;
		align-content: flex-start;
		gap: ${({ $gap }) => $gap}px;
		padding: 0 ${({ $padding }) => $padding}px;
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};

		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			margin-bottom: calc(${({ theme }) => theme.spacings.xxxlarge} + ${({ theme }) => theme.spacings.xxsmall});
		}
	`;

	export const ButtonContainer = styled.div`
		width: 380px;
		display: flex;
		flex-direction: row;
		gap: ${({ theme }) => theme.spacings.small};
		padding: ${({ theme }) =>
			`${theme.spacings.small} ${theme.spacings.small} ${theme.spacings.large} ${theme.spacings.small}`};
		border-top-width: ${({ theme }) => theme.border.width.medium};
		border-top-color: ${({ theme }) => theme.colors.borders.main.divider};
		background: ${({ theme }) => theme.colors.backgrounds.main.translucent};
	`;
}
